import { EmblaCarouselType } from "embla-carousel";
import useEmblaCarousel from "embla-carousel-react";
import React, { useCallback, useEffect, useState } from "react";

import { Media } from "../../contexts/media/MediaContext";
import {
  DynamicCardContent,
  DynamicCardGridContent,
  DynamicTestimonialContent,
  FeatureToggle,
  IconPack
} from "../../models/app-data-model";
import DynamicCard from "../dynamic-card/dynamic-card";
import ShIcon from "../sh-icon/sh-icon";
import TestimonialCard from "../testimonial-card/testimonial-card";
import "./dynamic-card-slider.scss";

export type CardSliderProps = {
  content: DynamicCardGridContent;
  featureToggles: FeatureToggle[];
  iconPack: IconPack[];
};

const DynamicCardSlider: React.FC<CardSliderProps> = ({ content, iconPack }: CardSliderProps) => {
  const sliderCards = Object?.values(content)
    .filter((cardTypeArray) => typeof cardTypeArray == "object" && cardTypeArray.length > 0)
    .shift();
  const [sliderRef, sliderApi] = useEmblaCarousel({ loop: false, align: "start", dragFree: true });
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const scrollTo = useCallback((index: number) => sliderApi?.scrollTo(index), [sliderApi]);

  const onInit = useCallback((sliderApi: EmblaCarouselType) => {
    if (window.innerWidth < 960) {
      setScrollSnaps(sliderCards);
    } else {
      setScrollSnaps(sliderApi?.scrollSnapList());
    }
  }, []);

  const onSelect = useCallback((sliderApi: EmblaCarouselType) => {
    setSelectedIndex(sliderApi.selectedScrollSnap());
  }, []);

  const scrollPrev = () => sliderApi?.scrollPrev();
  const scrollNext = () => sliderApi?.scrollNext();

  useEffect(() => {
    if (!sliderApi) return;

    onInit(sliderApi);
    onSelect(sliderApi);
    sliderApi.on("reInit", onInit);
    sliderApi.on("resize", onInit);
    sliderApi.on("select", onSelect);
  }, [sliderApi, onInit, onSelect]);

  const disablePrevBtn = !sliderApi?.canScrollPrev();
  const disableNextBtn = !sliderApi?.canScrollNext();

  return (
    <div className="dynamic-card-slider" ref={sliderRef} data-testid="dynamic-card-slider">
      <div className="slider-container" data-testid="slider-container">
        {sliderCards?.map((card: DynamicCardContent | DynamicTestimonialContent, index: number) => (
          <div className="slide" key={index} onClick={() => scrollTo(index)} data-testid="slide">
            {content.card_type != "Testimonial" ? (
              <DynamicCard
                key={`card-slider-card-${index}`}
                card_type={content.card_type}
                card_style={content.card_content_style}
                card_content={card as DynamicCardContent}
                card_content_size={content.card_content_size}
                iconPack={iconPack}
              />
            ) : (
              <TestimonialCard
                card_style={content.card_content_style}
                card_content={card as DynamicTestimonialContent}
                card_content_size={content.card_content_size}
              />
            )}
          </div>
        ))}
      </div>
      <Media lessThan="desktop">
        <div className="slider-controls">
          <button className={`prev-btn ${disablePrevBtn && "disabled-btn"}`} onClick={scrollPrev}>
            <ShIcon iconPack={iconPack} iconName="arrow" className="slider-left-arrow" altText="Back arrow icon" />
          </button>
          <div className="slider-pos-indicator" data-testid="slider-pos-indicator">
            {content &&
              scrollSnaps?.map((_, index) => (
                <button
                  type="button"
                  key={index}
                  id="slide-pos-btn"
                  onClick={() => scrollTo(index)}
                  className={`unstyled-btn ${"slide-pos".concat(index === selectedIndex ? " slide-pos--selected" : "")}`}
                ></button>
              ))}
          </div>
          <button className={`next-btn ${disableNextBtn && "disabled-btn"}`} onClick={scrollNext}>
            <ShIcon iconPack={iconPack} iconName="arrow" className="slider-right-arrow" altText="Forward arrow icon" />
          </button>
        </div>
      </Media>
      <Media greaterThanOrEqual="desktop">
        <div className="slider-controls">
          <button className={`prev-btn ${disablePrevBtn && "disabled-btn"}`} onClick={scrollPrev} data-testid="prev-btn">
            <ShIcon iconPack={iconPack} iconName="arrow" className="slider-left-arrow" altText="Back arrow icon" />
          </button>
          <button className={`next-btn ${disableNextBtn && "disabled-btn"}`} onClick={scrollNext} data-testid="next-btn">
            <ShIcon iconPack={iconPack} iconName="arrow" className="slider-right-arrow" altText="Forward arrow icon" />
          </button>
          <div className="slider-pos-indicator">
            {content &&
              scrollSnaps?.map((_, index) => (
                <button
                  type="button"
                  key={index}
                  id="slide-pos-btn"
                  onClick={() => scrollTo(index)}
                  className={`unstyled-btn ${"slide-pos".concat(index === selectedIndex ? " slide-pos--selected" : "")}`}
                ></button>
              ))}
          </div>
        </div>
      </Media>
    </div>
  );
};

export default DynamicCardSlider;
